"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import {
  isRecordingActive,
  RECORDING_HEARTBEAT_KEY,
  recordingActiveInAnyTab,
  useMeetingRecordingStore,
} from "@/stores/use-meeting-recording-store"
import { useAuth } from "@clerk/clerk-react"
import { useQueryClient } from "@tanstack/react-query"

import { useFetchCurrentCompany } from "@/hooks/api/companies"
import { GenericDialog } from "@/components/dialogs"

const ACTIVITY_KEY = "slant:last-active-at"
const LOGOUT_KEY = "slant:logged-out"
const QUERY_CACHE_KEY = "slant-query-cache"
const WARNING_BEFORE_MS = 60_000
const CHECK_INTERVAL_MS = 1_000
const WRITE_THROTTLE_MS = 5_000
const ACTIVITY_EVENTS = ["mousedown", "keydown", "touchstart", "scroll", "wheel"] as const

type LogoutReason = "timeout" | "manual"

function logoutReason(value: string): LogoutReason {
  try {
    return JSON.parse(value).reason === "manual" ? "manual" : "timeout"
  } catch {
    return "timeout"
  }
}

export function InactivityLock() {
  const { data: company } = useFetchCurrentCompany()
  const { signOut } = useAuth()
  const queryClient = useQueryClient()
  const recordingActive = useMeetingRecordingStore(isRecordingActive)

  const timeoutMs = company?.session_timeout_minutes ? company.session_timeout_minutes * 60_000 : null

  const [secondsLeft, setSecondsLeft] = useState<number | null>(null)
  const [locked, setLocked] = useState(false)
  const lastActiveRef = useRef(Date.now())
  const lastWriteRef = useRef(0)
  const lastHeartbeatRef = useRef(0)
  const lockingRef = useRef(false)

  const recordingElsewhere = useCallback(() => recordingActiveInAnyTab(), [])

  const persistRecordingHeartbeat = useCallback(() => {
    const now = Date.now()
    if (now - lastHeartbeatRef.current >= WRITE_THROTTLE_MS) {
      lastHeartbeatRef.current = now
      localStorage.setItem(RECORDING_HEARTBEAT_KEY, String(now))
    }
  }, [])

  const latestActivityAt = useCallback(() => {
    const stored = Number(localStorage.getItem(ACTIVITY_KEY))
    if (Number.isFinite(stored) && stored > lastActiveRef.current) lastActiveRef.current = stored
    return lastActiveRef.current
  }, [])

  const persistActivity = useCallback((force = false) => {
    const now = Date.now()
    lastActiveRef.current = now
    if (force || now - lastWriteRef.current >= WRITE_THROTTLE_MS) {
      lastWriteRef.current = now
      localStorage.setItem(ACTIVITY_KEY, String(now))
    }
    setSecondsLeft((previous) => (previous === null ? previous : null))
  }, [])

  const lockAndLogout = useCallback(
    async (reason: LogoutReason, broadcast = true) => {
      if (reason === "timeout" && (recordingActive || recordingElsewhere())) {
        persistActivity(true)
        return
      }
      if (lockingRef.current) return
      lockingRef.current = true
      setLocked(true)
      if (broadcast) localStorage.setItem(LOGOUT_KEY, JSON.stringify({ reason, at: Date.now() }))
      queryClient.clear()
      localStorage.removeItem(QUERY_CACHE_KEY)
      localStorage.removeItem(ACTIVITY_KEY)
      document.cookie = "dev_session_token=; path=/; max-age=0"
      await signOut().catch(() => undefined)
      window.location.assign("/")
    },
    [persistActivity, queryClient, recordingActive, recordingElsewhere, signOut]
  )

  const recordActivity = useCallback(
    (force = false) => {
      const now = Date.now()
      if (timeoutMs && !recordingActive && !recordingElsewhere() && now - latestActivityAt() >= timeoutMs) {
        void lockAndLogout("timeout")
        return
      }
      persistActivity(force)
    },
    [latestActivityAt, lockAndLogout, persistActivity, recordingActive, recordingElsewhere, timeoutMs]
  )

  useEffect(
    () =>
      useMeetingRecordingStore.subscribe((state, previous) => {
        const active = isRecordingActive(state)
        const wasActive = isRecordingActive(previous)
        if (active && !wasActive) {
          persistActivity(true)
          localStorage.setItem(RECORDING_HEARTBEAT_KEY, String(Date.now()))
        } else if (!active && wasActive) {
          localStorage.removeItem(RECORDING_HEARTBEAT_KEY)
          persistActivity(true)
        }
      }),
    [persistActivity]
  )

  useEffect(() => {
    if (!timeoutMs) {
      setSecondsLeft(null)
      return
    }

    const stored = Number(localStorage.getItem(ACTIVITY_KEY))
    if (Number.isFinite(stored)) lastActiveRef.current = Math.max(lastActiveRef.current, stored)
    if (recordingActive) persistActivity(true)

    const onActivity = () => recordActivity()
    ACTIVITY_EVENTS.forEach((event) => window.addEventListener(event, onActivity, { passive: true, capture: true }))

    const onStorage = (event: StorageEvent) => {
      if (event.key === LOGOUT_KEY && event.newValue) {
        void lockAndLogout(logoutReason(event.newValue), false)
        return
      }
      if (event.key === ACTIVITY_KEY && event.newValue) {
        const value = Number(event.newValue)
        if (Number.isFinite(value)) {
          lastActiveRef.current = Math.max(lastActiveRef.current, value)
          setSecondsLeft((prev) => (prev === null ? prev : null))
        }
      }
    }
    window.addEventListener("storage", onStorage)

    const interval = window.setInterval(() => {
      if (recordingActive) {
        persistRecordingHeartbeat()
        persistActivity()
        return
      }

      if (recordingElsewhere()) {
        setSecondsLeft((prev) => (prev === null ? prev : null))
        return
      }

      const idle = Date.now() - latestActivityAt()
      if (idle >= timeoutMs) {
        void lockAndLogout("timeout")
      } else if (idle >= timeoutMs - WARNING_BEFORE_MS) {
        setSecondsLeft(Math.max(1, Math.ceil((timeoutMs - idle) / 1000)))
      } else {
        setSecondsLeft((prev) => (prev === null ? prev : null))
      }
    }, CHECK_INTERVAL_MS)

    return () => {
      ACTIVITY_EVENTS.forEach((event) => window.removeEventListener(event, onActivity, { capture: true }))
      window.removeEventListener("storage", onStorage)
      window.clearInterval(interval)
    }
  }, [
    latestActivityAt,
    lockAndLogout,
    persistActivity,
    persistRecordingHeartbeat,
    recordActivity,
    recordingActive,
    recordingElsewhere,
    timeoutMs,
  ])

  if (locked) {
    return (
      <div className="bg-background fixed inset-0 z-[100] flex items-center justify-center">
        <p className="text-muted-foreground text-sm">Signing you out…</p>
      </div>
    )
  }

  return (
    <GenericDialog
      title={"Still there?"}
      open={secondsLeft !== null}
      onOpenChange={(open) => !open && recordActivity(true)}
      dismiss="explicit"
      description={
        <>
          You&apos;ll be signed out in {secondsLeft ?? 0} second
          {secondsLeft === 1 ? "" : "s"} due to inactivity.
        </>
      }
      action={{ label: "Stay signed in", onClick: () => recordActivity(true), closeAfterClick: true }}
      secondaryAction={{ label: "Sign out now", onClick: () => lockAndLogout("manual") }}
    ></GenericDialog>
  )
}
